import { ADMIN_LIST_DEFAULT_PAGE_SIZE } from "@etudescarre/constants";
import { isNil } from "@etudescarre/utils";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";

export type AdminListState = {
  search: string;
  page: number;
  pageSize: number;
};

export type AdminListActions = {
  setSearch: (value: string) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
};

export type UseAdminListQueryResult<T> = AdminListState &
  AdminListActions & {
    items: T[];
    total: number;
    loading: boolean;
    error: string | null;
    refetch: () => void;
  };

export type AdminListFetcher<T> = (params: {
  search?: string;
  page: number;
  pageSize: number;
}) => Promise<{ items: T[]; total: number }>;

const DEBOUNCE_MS = 300;

export function useAdminListQuery<T>(
  fetcher: AdminListFetcher<T>,
  queryKey: string,
): UseAdminListQueryResult<T> {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-synced state
  const search = searchParams.get(`${queryKey}_search`) ?? "";
  const page = Number(searchParams.get(`${queryKey}_page`) ?? 1);
  const pageSize = Number(
    searchParams.get(`${queryKey}_pageSize`) ?? ADMIN_LIST_DEFAULT_PAGE_SIZE,
  );

  // Local state for data
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Debounce the search input - debounced value is what gets fetched
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Increment to force a re-fetch without changing other state
  const [refetchCounter, setRefetchCounter] = useState(0);

  // When URL search param changes, debounce the actual fetch
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [search]);

  // Fetch data when debounced search, page, or pageSize changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: refetchCounter is intentionally used as a trigger and not referenced inside the effect body
  useEffect(() => {
    let cancelled = false;

    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetcher({
          search: debouncedSearch || undefined,
          page,
          pageSize,
        });
        if (!cancelled) {
          setItems(result.items);
          setTotal(result.total);
        }
      } catch {
        if (!cancelled) {
          setError("Erreur lors du chargement des données");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetch();

    return () => {
      cancelled = true;
    };
  }, [fetcher, debouncedSearch, page, pageSize, refetchCounter]);

  const updateParams = useCallback(
    (updates: Partial<Record<string, string | null>>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (isNil(value) || value === "") {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setSearch = useCallback(
    (value: string) => {
      // Reset to page 1 when search changes
      updateParams({
        [`${queryKey}_search`]: value || null,
        [`${queryKey}_page`]: null,
      });
    },
    [updateParams, queryKey],
  );

  const setPage = useCallback(
    (newPage: number) => {
      updateParams({ [`${queryKey}_page`]: String(newPage) });
    },
    [updateParams, queryKey],
  );

  const setPageSize = useCallback(
    (newPageSize: number) => {
      updateParams({
        [`${queryKey}_pageSize`]: String(newPageSize),
        [`${queryKey}_page`]: null,
      });
    },
    [updateParams, queryKey],
  );

  const refetch = useCallback(() => {
    // Increment counter to force the fetch effect to re-run
    setRefetchCounter((c) => c + 1);
  }, []);

  return {
    items,
    total,
    loading,
    error,
    search,
    page,
    pageSize,
    setSearch,
    setPage,
    setPageSize,
    refetch,
  };
}
